import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, Download, MoreVertical, 
  ChevronLeft, ChevronRight, Mail, CheckCircle, 
  XCircle, Shield, User as UserIcon, Trash2
} from 'lucide-react';
import api from '../../services/api';
import Navbar from '../../components/Navbar';
import Loading from '../../components/Loading';
import toast from 'react-hot-toast';

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  const page = parseInt(searchParams.get('page')) || 1;
  const status = searchParams.get('status') || '';
  const role = searchParams.get('role') || '';

  useEffect(() => {
    fetchUsers();
  }, [page, status, role, searchParams]);

  useEffect(() => {
    const closeMenu = () => setActionMenuOpen(null);

    document.addEventListener('click', closeMenu);

    return () => {
      document.removeEventListener('click', closeMenu);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(search && { search }),
        ...(status && { status }),
        ...(role && { role })
      });
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ ...Object.fromEntries(searchParams), search, page: 1 });
    fetchUsers();
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/export/users', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Kullanıcılar başarıyla dışa aktarıldı');
    } catch (err) {
      toast.error('Kullanıcılar dışa aktarılamadı');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      toast.success('Rol başarıyla güncellendi');
      fetchUsers();
    } catch (err) {
      toast.error('Rol güncellenemedi');
    }
    setActionMenuOpen(null);
  };

  const handleVerifyEmail = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/verify`);
      toast.success('E-posta doğrulandı');
      fetchUsers();
    } catch (err) {
      toast.error('E-posta doğrulanamadı');
    }
    setActionMenuOpen(null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (err) {
      toast.error('Kullanıcı silinemedi');
    }
    setActionMenuOpen(null);
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <Navbar />
      
      <div className="container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Kullanıcı Yönetimi</h1>
            <p className="text-gray">{pagination.total || 0} toplam kullanıcı</p>
          </div>
          <button onClick={handleExport} className="btn btn-outline">
            <Download size={18} /> CSV Dışa Aktar
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="card-body p-4">
            <div className="flex items-center gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Ad, e-posta veya okul ile ara..."
                    className="form-input"
                    style={{ paddingLeft: '40px' }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </form>
              <select 
                className="form-select" 
                style={{ width: 'auto' }}
                value={status}
                onChange={(e) => setSearchParams({ ...Object.fromEntries(searchParams), status: e.target.value, page: 1 })}
              >
                <option value="">Tüm Durumlar</option>
                <option value="verified">Doğrulandı</option>
                <option value="unverified">Doğrulanmadı</option>
              </select>
              <select 
                className="form-select" 
                style={{ width: 'auto' }}
                value={role}
                onChange={(e) => setSearchParams({ ...Object.fromEntries(searchParams), role: e.target.value, page: 1 })}
              >
                <option value="">Tüm Roller</option>
                <option value="user">Kullanıcı</option>
                <option value="admin">Yönetici</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card">
          <div className="card-body p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>Kullanıcı</th>
                  <th>Okul</th>
                  <th>Durum</th>
                  <th>İlerleme</th>
                  <th>Rol</th>
                  <th>Kayıt Tarihi</th>
                  <th style={{ width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div style={{ 
                          width: '36px', 
                          height: '36px', 
                          borderRadius: '50%', 
                          backgroundColor: '#e0f2fe',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#1a5276'
                        }}>
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <Link to={`/admin/users/${user.id}`} className="font-medium">
                            {user.name}
                          </Link>
                          <p className="text-xs text-gray">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-gray">{user.school || '-'}</td>
                    <td>
                      {user.email_verified ? (
                        <span className="badge badge-success">
                          <CheckCircle size={12} /> Doğrulandı
                        </span>
                      ) : (
                        <span className="badge badge-warning">
                          <XCircle size={12} /> Unverified
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ width: '100px' }}>
                        <div className="flex items-center gap-2">
                          <div style={{ 
                            flex: 1,
                            height: '6px', 
                            backgroundColor: '#e2e8f0', 
                            borderRadius: '3px'
                          }}>
                            <div style={{ 
                              width: `${user.completion_percentage || 0}%`, 
                              height: '100%', 
                              backgroundColor: user.completion_percentage === 100 ? '#059669' : '#1a5276',
                              borderRadius: '3px'
                            }}></div>
                          </div>
                          <span className="text-xs text-gray">{user.completion_percentage || 0}%</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${user.role === 'admin' ? 'badge-info' : 'badge-gray'}`}>
                        {user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                      </span>
                    </td>
                    <td className="text-gray text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ position: 'relative' }}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuOpen(
                              actionMenuOpen === user.id ? null : user.id
                            );
                          }}
                          className="btn btn-sm btn-ghost"
                        >
                          <MoreVertical size={16} />
                        </button>
                        
                        {actionMenuOpen === user.id && (
                          <div className="" style={{ 
                            position: 'absolute', 
                            right: 0, 
                            top: '100%',
                            zIndex: 99999,
                            display: 'block',
                            visibility: 'visible',
                            opacity: 1,
                            background: '#fff',
                            minWidth: '220px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 10px 20px rgba(0,0,0,.15)'
                          }}>
                            <Link to={`/admin/users/${user.id}`} className="dropdown-item">
                              <UserIcon size={14} /> Detayları Gör
                            </Link>
                            {!user.email_verified && (
                              <button onClick={() => handleVerifyEmail(user.id)} className="dropdown-item">
                                <Mail size={14} /> E-Postayı Doğrula
                              </button>
                            )}
                            {user.role === 'user' ? (
                              <button onClick={() => handleRoleChange(user.id, 'admin')} className="dropdown-item">
                                <Shield size={14} /> Yönetici Yap
                              </button>
                            ) : (
                              <button onClick={() => handleRoleChange(user.id, 'user')} className="dropdown-item">
                                <UserIcon size={14} /> Yöneticiliği Kaldır
                              </button>
                            )}
                            <button onClick={() => handleDeleteUser(user.id)} className="dropdown-item text-danger">
                              <Trash2 size={14} /> Kullanıcıyı Sil
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="p-8 text-center text-gray">
                Arama kriterlerine uygun kullanıcı bulunamadı.
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="card-footer flex items-center justify-between">
              <p className="text-sm text-gray">
                Gösterilen kullanıcılar: {((page - 1) * 20) + 1} - {Math.min(page * 20, pagination.total)} / Toplam: {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: page - 1 })}
                  disabled={page === 1}
                  className="btn btn-sm btn-outline"
                >
                  <ChevronLeft size={16} /> Önceki
                </button>
                <span className="text-sm text-gray">Sayfa {page} / {pagination.pages}</span>
                <button 
                  onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: page + 1 })}
                  disabled={page === pagination.pages}
                  className="btn btn-sm btn-outline"
                >
                  Sonraki <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
